import {MaterialReactTable, MRT_ColumnDef, MRT_RowData, MRT_TableOptions, useMaterialReactTable} from "material-react-table";
import {useMemo} from "react";
import {tableHeaderGray} from "../../../constants/colors";

import {argosFilteringOptions, postProcessColumns, TABLE_DEFAULT_PAGE_INDEX, TABLE_DEFAULT_PAGE_SIZE} from "./common";
import type {Column} from "@tanstack/table-core";

/*************************************************************************************
 * Reusable component used for the more basic tables throughout the apps in Argos
 *
 * They include basic functionality found throughout all tables such as column
 * actions, filters, sorting, resizing. They do not have the same formatting and
 * coloring as the main tables
 *************************************************************************************/
interface IArgosSimpleTableProps<TData extends MRT_RowData> extends MRT_TableOptions<TData> {
  columns: MRT_ColumnDef<TData>[];
  data: TData[];
}

export const ArgosTableSimple = <TData extends MRT_RowData>({
                                                              columns,
                                                              data,
                                                              ...rest
                                                            }: IArgosSimpleTableProps<TData>) => {
  const processedColumns = useMemo(() => {
    return postProcessColumns(columns)
  }, [columns])

  const table = useMaterialReactTable({
    columns: processedColumns,
    data,

    // Global filter
    globalFilterFn: 'contains', // default global filter function
    enableGlobalFilter: true, // enable global filter by default
    getColumnCanGlobalFilter: (column: Column<any, unknown>) => {
      // Only allow global filtering on columns that are visible
      return column.getIsVisible()
    },

    columnFilterModeOptions: argosFilteringOptions,
    muiToolbarAlertBannerProps: {
      // Footer row style
      sx: {
        height: 'auto !important',
      },
    },
    enablePagination: false,                 //pagination at bottom of table
    autoResetPageIndex: false,

    enableColumnPinning: false,

    //Active Props
    enableColumnResizing: true,             //resizing columns
    enableFullScreenToggle: false,          //full screen capability and button
    enableDensityToggle: false,             //change density of table with button
    enableColumnFilterModes: true,          //switch between multiple filter modes
    filterFromLeafRows: true,               //include parent rows when children are included in filter
    positionToolbarAlertBanner: "bottom",   //banners in toolbar show below button icon
    enableStickyHeader: true,               //keeps header visible while scrolling

    muiCircularProgressProps: {
      color: 'info',
      thickness: 5,
      size: 55,
    },
    muiTableHeadCellProps: {
      // Header cell text style
      sx: {
        // Left justify action button when visible
        '& .Mui-TableHeadCell-Content': {
          justifyContent: 'left',
        },
        color: 'black',         //header font color
        fontWeight: 'bold',     //header font weight
      },
    },
    muiTableHeadRowProps: {
      // Header row style
      sx: {
        backgroundColor: tableHeaderGray,
      },
    },

    muiTableContainerProps: ({table}) => {
      return ({
        sx: {
          maxHeight: `calc(100% - ${table.refs.topToolbarRef.current?.offsetHeight ?? 0}px - ${table.refs.bottomToolbarRef.current?.offsetHeight ?? 0}px - ${table.refs.tableFooterRef.current?.offsetHeight ?? 0}px - 50px) !important`,
        },
      })
    },
    muiTablePaperProps: {
      sx: {
        height: '100% !important',
        minWidth: '0px !important',
        maxWidth: "initial !important",
      },
    },

    ...rest, //accept props to override default table options

    initialState: {pagination: {pageSize: TABLE_DEFAULT_PAGE_SIZE, pageIndex: TABLE_DEFAULT_PAGE_INDEX}, ...rest.initialState},
  });


  return <MaterialReactTable table={table}/>;
};